import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import swal from "sweetalert";
import PaymentProviderModal from "./add&updatePaymentProviderModal";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import "./PaymentProviderFilters.css";

const ManagePaymentProviders = () => {
  const [search, setSearch] = useState("");
  const [countryCodeFilter, setCountryCodeFilter] = useState(null);
  const pageKey = "paymentProviders";
  const defaultVisibleColumns = {
    sortOrder: true,
    countryCode: true,
    active: true,
    createdDate: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [providerToEdit, setProviderToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const sortByOptions = [
    { value: "ID", label: "ID" },
    { value: "Name", label: "Name" },
    { value: "SortOrder", label: "Sort Order" },
    { value: "CountryCode", label: "Country Code" },
    { value: "CreatedDate", label: "Created Date" },
  ];

  const clearFilters = () => {
    setSearch("");
    setCountryCodeFilter(null);
  };

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_ALL_PAYMENT_PROVIDERS,
        {
          params: {
            Search: search || "",
            CountryCode: countryCodeFilter?.value || "",
            SortBy: sortBy.value,
            SortOrder: sortOrder.value,
          },
        }
      );

      const providerData = response.data.data || [];
      if (Array.isArray(providerData)) {
        setProviders(providerData);
      } else {
        setProviders([]);
      }
    } catch (error) {
      setProviders([]);
      showErrorToast("Failed to fetch online payment methods.");
    } finally {
      setLoading(false);
    }
  }, [search, countryCodeFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handleProviderSubmit = async (providerData, isUpdate, isBulkEdit = false) => {
    try {
      setLoading(true);

      if (isBulkEdit) {
        // Handle bulk edit - update each provider
        const updatePromises = providerData.providerIds.map(async (id) => {
          const updateData = {
            ID: id,
            Active: providerData.active,
          };

          if (providerData.name) {
            updateData.Name = providerData.name;
          }
          if (providerData.countryCode) {
            updateData.CountryCode = providerData.countryCode;
          }

          return axiosInstance.post(APIs.POST_ADD_UPDATE_PAYMENT_PROVIDER, updateData);
        });

        await Promise.all(updatePromises);
        setSelectedItems(new Set());
        lastSelectedIndex.current = null;
        fetchProviders();
        showSuccessToast(`${providerData.providerIds.length} Online payment methods updated successfully.`);
      } else {
        // Handle single add/update
        const response = await axiosInstance.post(
          APIs.POST_ADD_UPDATE_PAYMENT_PROVIDER,
          providerData
        );

        if (response.data.status) {
          const updatedProvider = response.data.data;
          if (isUpdate) {
            setProviders((prev) =>
              prev.map((provider) =>
                provider.id === updatedProvider.id ? updatedProvider : provider
              )
            );
          } else {
            setProviders((prev) => [...prev, updatedProvider]);
          }
          showSuccessToast(`Payment Provider ${isUpdate ? "Updated" : "Added"}.`);
        } else {
          showErrorToast(response.data.message || "Operation failed.");
        }
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showErrorToast("A payment provider with this name and country code already exists.");
      } else {
        showErrorToast("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddProviderClick = () => {
    setProviderToEdit(null);
    setShowAddProviderModal(true);
  };

  const handleEditProviderClick = (provider) => {
    setProviderToEdit(provider);
    setShowAddProviderModal(true);
  };

  const DeleteProvider = async (provider) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${provider.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    setLoading(true);
    try {
      const response = await axiosInstance.delete(
        APIs.DELETE_PAYMENT_PROVIDER,
        {
          params: {
            ID: provider.id,
          },
        }
      );
      if (response.data.status) {
        setProviders((prev) =>
          prev.filter((p) => p.id !== provider.id)
        );
        showSuccessToast("Payment Provider is Deleted.");
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (providerId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (providers[i]?.id) {
            newSelected.add(providers[i].id);
          }
        }
      } else {
        if (newSelected.has(providerId)) {
          newSelected.delete(providerId);
        } else {
          newSelected.add(providerId);
        }
        lastSelectedIndex.current = index;
      }
      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(providers.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No Online payment methods selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected Online payment methods?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((providerId) =>
          axiosInstance.delete(APIs.DELETE_PAYMENT_PROVIDER, {
            params: {
              ID: providerId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchProviders();
      showSuccessToast(
        `${selectedItems.size}Online payment methods deleted successfully.`
      );
    } catch (err) {
      showErrorToast("Failed to delete selected Online payment methods.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No Online payment methods selected for editing.");
      return;
    }

    const selectedProviders = providers.filter((provider) =>
      selectedItems.has(provider.id)
    );
    setProviderToEdit(selectedProviders);
    setShowAddProviderModal(true);
  };

  const toggleProviderStatus = async (provider) => {
    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_PAYMENT_PROVIDER_STATUS,
        null,
        {
          params: {
            ID: provider.id,
            Active: !provider.active,
          },
        }
      );

      if (response.data.status) {
        setProviders((prev) =>
          prev.map((p) =>
            p.id === provider.id ? { ...p, active: !provider.active } : p
          )
        );
        showSuccessToast(
          `Online Payment Method ${!provider.active ? "activated" : "deactivated"} successfully.`
        );
      } else {
        showErrorToast(response.data.message);
      }
    } catch (err) {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleDragStart = (e, provider) => {
    const index = providers.findIndex((p) => p.id === provider.id);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newProviders = [...providers];
    const draggedProvider = newProviders[draggedIndex];
    newProviders.splice(draggedIndex, 1);
    newProviders.splice(overIndex, 0, draggedProvider);
    newProviders = newProviders.map((provider, index) => ({
      ...provider,
      sortOrder: index + 1,
    }));

    setProviders(newProviders);

    try {
      await axiosInstance.post(
        APIs.POST_UPDATE_PAYMENT_PROVIDER_SORT_ORDER,
        {
          Updates: newProviders.map((provider) => ({
            ID: provider.id,
            SortOrder: provider.sortOrder,
          })),
        }
      );
      fetchProviders();
      showSuccessToast("Online payment method order updated successfully.");
    } catch (error) {
      showErrorToast("Failed to update online payment method order.");
      fetchProviders();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">
              Manage Online Payment Methods
            </li>
          </ol>
          <h1 className="page-header mb-0">Manage Online Payment Methods</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddProviderClick}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "25px",
              fontWeight: "500"
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
            Add Online Payment Method
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {providers.length > 0 && (
          <div className="card border-0">
            <div className="tab-content p-3">
              <div className="tab-pane fade show active" id="allTab">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder="Search by name..."
                              style={{
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: isDarkMode ? '#ffffff' : '#333'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                              options={sortByOptions}
                              styles={dropdownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                              styles={dropdownStyles}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={clearFilters}
                              title="Clear all filters"
                              style={{
                                height: "32px",
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: 'white',
                                borderRadius: '6px'
                              }}
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when providers are selected) */}
                {selectedItems.size > 0 && (
                  <div className="mb-3">
                    <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold text-primary">
                          {selectedItems.size} online payment method{selectedItems.size > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleDeselectAll}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Online Payment Methods"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                            title="Delete Selected Online Payment Methods"
                        >
                          <i className="fa fa-trash me-1"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2">
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              providers.length > 0 &&
                              selectedItems.size === providers.length
                            }
                          />
                        </th>
                        <th className="pt-2 pb-2">Name</th>
                        {visibleColumns.sortOrder && (
                          <th className="pt-2 pb-2 text-center">Sort Order</th>
                        )}
                        {visibleColumns.countryCode && (
                          <th className="pt-2 pb-2">Country Code</th>
                        )}
                        {visibleColumns.active && (
                          <th className="pt-2 pb-2 text-center">Status</th>
                        )}
                        {visibleColumns.createdDate && (
                          <th className="pt-2 pb-2">Created Date</th>
                        )}
                        <th className="pt-2 pb-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {providers.map((provider, index) => (
                        <tr
                          key={provider.id}
                          draggable={false}
                          onDragStart={(e) => {
                            // Only allow dragging from the grab icon
                            if (!e.target.classList.contains('fa-bars')) {
                              e.preventDefault();
                            }
                          }}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragLeave={handleDragLeave}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                        >
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(provider.id)}
                              onChange={(e) =>
                                handleSelectItem(provider.id, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => handleEditProviderClick(provider)}
                            style={{ cursor: "pointer" }}
                          >
                            {provider.name}
                          </td>
                          {visibleColumns.sortOrder && (
                            <td className="align-middle text-center">
                              {provider.sortOrder}
                            </td>
                          )}
                          {visibleColumns.countryCode && (
                            <td className="align-middle">{provider.countryCode}</td>
                          )}
                          {visibleColumns.active && (
                            <td className="align-middle text-center">
                              <span
                                className={`badge ${
                                  provider.active ? "bg-success" : "bg-secondary"
                                }`}
                              >
                                {provider.active ? "Active" : "Inactive"}
                              </span>
                            </td>
                          )}
                          {visibleColumns.createdDate && (
                            <td className="align-middle">
                              {formatDate(provider.createdDate)}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Drag Handle */}
                              <i
                                className="fas fa-bars text-secondary"
                                style={{
                                  cursor: "grab",
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #6c757d',
                                  backgroundColor: 'transparent',
                                  transition: 'all 0.2s ease'
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, provider)}
                                onDragEnd={handleDragEnd}
                                title="Drag to reorder"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#6c757d';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#6c757d';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              ></i>

                              {/* Edit Button */}
                              <button
                                className="btn btn-sm btn-outline-primary"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                  transition: 'all 0.2s ease',
                                  padding: 0
                                }}
                                onClick={() => handleEditProviderClick(provider)}
                                title="Edit Payment Provider"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                              </button>

                              {/* Toggle Status Button */}
                              <button
                                className="btn btn-sm"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: provider.active ? '1px solid #ffc107' : '1px solid #28a745',
                                  color: provider.active ? '#ffc107' : '#28a745',
                                  backgroundColor: 'transparent',
                                  transition: 'all 0.2s ease',
                                  padding: 0
                                }}
                                onClick={() => toggleProviderStatus(provider)}
                                title={
                                  provider.active
                                    ? "Deactivate Provider"
                                    : "Activate Provider"
                                }
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = provider.active ? '#ffc107' : '#28a745';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = provider.active ? '#ffc107' : '#28a745';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i
                                  className={`fa ${
                                    provider.active ? "fa-ban" : "fa-check"
                                  }`}
                                  style={{ fontSize: '12px' }}
                                ></i>
                              </button>

                              {/* Delete Button */}
                              <button
                                className="btn btn-sm btn-outline-danger"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease',
                                  padding: 0
                                }}
                                onClick={() => DeleteProvider(provider)}
                                title="Delete Payment Provider"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <PaymentProviderModal
        isModalOpen={showAddProviderModal}
        setIsModalOpen={setShowAddProviderModal}
        onPaymentProviderSubmit={handleProviderSubmit}
        paymentProviderToEdit={providerToEdit}
        isBulkEdit={Array.isArray(providerToEdit)}
      />
    </div>
  );
};

export default ManagePaymentProviders;

